import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSubscriptionStore, type StripePriceInfo } from '@/common/stores/useSubscriptionStore';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID_MONTHLY;
const ANNUAL_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID_ANNUAL;

const FEATURE_KEYS = [
  'upgradeModal.feature1',
  'upgradeModal.feature2',
  'upgradeModal.feature3',
  'upgradeModal.feature4',
  'upgradeModal.feature5',
  'upgradeModal.feature6',
] as const;

const formatPrice = (price: StripePriceInfo, locale?: string): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: price.currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price.amount);
};

export const UpgradeModal = ({ isOpen, onClose }: UpgradeModalProps) => {
  const { t, i18n } = useTranslation();
  const { createCheckoutSession, prices } = useSubscriptionStore();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const monthlyPrice = MONTHLY_PRICE_ID ? prices[MONTHLY_PRICE_ID] : undefined;
  const annualPrice = ANNUAL_PRICE_ID ? prices[ANNUAL_PRICE_ID] : undefined;
  const activePrice = billingPeriod === 'monthly' ? monthlyPrice : annualPrice;

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const priceId = billingPeriod === 'monthly' ? MONTHLY_PRICE_ID : ANNUAL_PRICE_ID;
      const url = await createCheckoutSession(priceId);
      if (url) {
        window.location.href = url;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[60]" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden border border-gray-100">
          <div className="relative border-b border-gray-200 bg-gray-50 px-6 pt-6 pb-5">
            <button
              className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              onClick={onClose}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100">
                <SparklesIcon className="h-5 w-5 text-gray-900" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">{t('upgradeModal.title')}</h2>
                <p className="text-sm text-gray-600 mt-0.5">{t('upgradeModal.subtitle')}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex bg-gray-100 rounded-xl p-1 mb-4">
              <button
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white shadow-sm text-gray-900 border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setBillingPeriod('monthly')}
              >
                {t('upgradeModal.monthly')}
              </button>
              <button
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  billingPeriod === 'annual'
                    ? 'bg-white shadow-sm text-gray-900 border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setBillingPeriod('annual')}
              >
                {t('upgradeModal.annual')}
                {monthlyPrice && annualPrice && (
                  <span className="ml-1.5 text-xs text-emerald-600 font-semibold">
                    {t('upgradeModal.savePct', { pct: Math.round((1 - annualPrice.amount / (monthlyPrice.amount * 12)) * 100) })}
                  </span>
                )}
              </button>
            </div>

            {activePrice && (
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-gray-900">
                    {formatPrice(activePrice, i18n.language)}
                  </span>
                  <span className="text-base text-gray-500">
                    /
                    {activePrice.interval === 'year'
                      ? t('upgradeModal.annual').toLowerCase()
                      : t('upgradeModal.monthly').toLowerCase()}
                  </span>
                </div>
                {billingPeriod === 'annual' && monthlyPrice && annualPrice && (
                  <p className="text-sm text-gray-500 mt-1">
                    {formatPrice(
                      { ...annualPrice, amount: annualPrice.amount / 12 },
                      i18n.language,
                    )}
                    /{t('upgradeModal.monthly').toLowerCase()}
                  </p>
                )}
              </div>
            )}

            <ul className="space-y-3 mb-6">
              {FEATURE_KEYS.map((key) => (
                <li key={key} className="flex items-start gap-3">
                  <CheckIcon className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{t(key)}</span>
                </li>
              ))}
            </ul>

            <button
              className="w-full py-3 px-4 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md active:scale-95"
              disabled={loading}
              onClick={handleUpgrade}
            >
              {loading ? t('upgradeModal.redirecting') : t('upgradeModal.continueToCheckout')}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              {t('upgradeModal.securePayment')}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
